import { useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const INVOICE_STATUS = [
  'draft',
  'pending-validation',
  'handled-by-accounting',
  'paid',
  'issue',
] as const;

export type InvoiceStatus = (typeof INVOICE_STATUS)[number];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const INVOICE_PAYMENT_STRATEGY = [
  'store-cash-out',
  'prolong-payout',
  'handled-by-accounting-team',
  'prolong-payin', // Client payment via Prolong (only Stripe for now)
] as const;

export type InvoicePaymentStrategy = (typeof INVOICE_PAYMENT_STRATEGY)[number];

export const getInvoiceOriginName = (invoice: {
  originCompany: null | {
    name: string;
  };
}) => {
  if (invoice.originCompany) {
    return invoice.originCompany.name;
  }

  return '-';
};

export const useInvoiceDestinationName = (invoice: {
  destinationClient: null | { name: string | null };
  destinationCompany: null | { name: string };
}) => {
  const { t } = useLingui();

  if (invoice.destinationClient) {
    return (
      invoice.destinationClient.name || t({ id: 'invoice.destination-client', message: 'Client' })
    );
  }

  if (invoice.destinationCompany) {
    return invoice.destinationCompany.name;
  }

  return '-';
};

export type InvoiceFromGet = Endpoints['GET /invoices/:id']['response'];
export const useInvoice = (id?: string) => {
  const fetch = useFetch<Endpoints['GET /invoices/:id']>();

  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => fetch(`/invoices/${id}`),
    enabled: !!id,
  });
};

export type InvoiceFromGetMany = Endpoints['GET /invoices']['response']['invoices'][number];
export const useInvoices = (
  params?: Endpoints['GET /invoices']['query'],
  options?: {
    enabled: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /invoices']>();

  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => fetch('/invoices', params),
    enabled: options?.enabled !== false,
  });
};

export const useCreateInvoice = () => {
  const fetch = useFetch<Endpoints['POST /invoices']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /invoices']['body']) =>
      fetch('/invoices', undefined, { method: 'POST', body }),
  });
};

export const useUpdateInvoice = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /invoices/:id']>();

  const invoiceQueryKey = ['invoices', id];

  return useMutation({
    mutationFn: async ({
      originCompany,
      destinationCompany,
      invoiceLines,
      ...data
    }: Omit<
      Endpoints['PATCH /invoices/:id']['body'],
      'originCompanyId' | 'destinationCompanyId' | 'invoiceLineIds'
    > & {
      originCompany?: InvoiceFromGet['originCompany'];
      destinationCompany?: InvoiceFromGet['destinationCompany'];
      invoiceLines?: InvoiceFromGet['lines'];
    }) =>
      fetch(`/invoices/${id}`, undefined, {
        method: 'PATCH',
        body: {
          ...data,
          originCompanyId: originCompany === null ? null : originCompany?.id,
          destinationCompanyId: destinationCompany === null ? null : destinationCompany?.id,
          invoiceLineIds: invoiceLines?.map(({ id }) => id),
        },
      }),
    onMutate: async ({ originCompany, destinationCompany, invoiceLines, ...data }) => {
      await queryClient.cancelQueries({ queryKey: invoiceQueryKey });

      const previousInvoice = queryClient.getQueryData<InvoiceFromGet>(invoiceQueryKey);

      if (!previousInvoice) {
        return;
      }

      const updatedInvoice = {
        ...previousInvoice,
        ...data,
        originCompany:
          originCompany === null ? null : (originCompany ?? previousInvoice.originCompany),
        destinationCompany:
          destinationCompany === null
            ? null
            : (destinationCompany ?? previousInvoice.destinationCompany),
        lines: invoiceLines ?? previousInvoice.lines,
      };

      queryClient.setQueryData(invoiceQueryKey, updatedInvoice);

      return { previousInvoice, updatedInvoice };
    },
    onError: (_err, _params, context) => {
      if (context?.previousInvoice) {
        queryClient.setQueryData(invoiceQueryKey, context.previousInvoice);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceQueryKey });
    },
  });
};

export const useSubmitInvoice = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /invoices/:id/submit']>();

  return useMutation({
    mutationFn: () => fetch(`/invoices/${id}/submit`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice-lines'] });
    },
  });
};

export const useValidateInvoice = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /invoices/:id/validate']>();

  return useMutation({
    mutationFn: () => fetch(`/invoices/${id}/validate`, undefined, { method: 'POST' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

export const useReportIssue = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /invoices/:id/report-issue']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /invoices/:id/report-issue']['body']) =>
      fetch(`/invoices/${id}/report-issue`, undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};

export const useDeleteInvoice = (id: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /invoices/:id']>();

  return useMutation({
    mutationFn: () => fetch(`/invoices/${id}`, undefined, { method: 'DELETE' }),
    onSettled: () => {
      queryClient.removeQueries({ queryKey: ['invoices', id] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};
